import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Film, HardDrive, Layers3, Loader2, RefreshCw, Search, ShieldAlert, Sparkles, Trash2, X, Youtube } from 'lucide-react';

import { sessionService, type SessionStorageItem, type SessionStorageSummary } from '../services/sessionService';

interface StorageCleanupManagerProps {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  isDarkMode: boolean;
}

interface StorageCleanupGroup {
  key: string;
  isFamily: boolean;
  familySlug: string | null;
  title: string;
  latest: SessionStorageItem;
  members: SessionStorageItem[];
  visibleMembers: SessionStorageItem[];
  totalBytes: number;
  totalCostUsd: number;
  eligibleBytes: number;
  eligibleCount: number;
  arrangementBlockedCount: number;
  blockedHiddenCount: number;
  selectableMemberIds: string[];
}

const StorageCleanupManager: React.FC<StorageCleanupManagerProps> = ({ isOpen, onClose, token, isDarkMode }) => {
  const [storageSummary, setStorageSummary] = useState<SessionStorageSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [hideArrangementBlocked, setHideArrangementBlocked] = useState(true);

  const loadSummary = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const summary = await sessionService.getSessionStorageSummary(token, undefined, 'all', {
        contentSource: 'all',
        contentVisibility: 'all',
        includeExternal: true,
      });
      setStorageSummary(summary);
    } catch (err: any) {
      setError(err.message || 'Failed to load hosted media inventory');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isOpen) return;
    void loadSummary();
  }, [isOpen, loadSummary]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedSessionIds(new Set());
      setExpandedGroupKeys(new Set());
      setSuccessMessage(null);
      setError(null);
      setHideArrangementBlocked(true);
    }
  }, [isOpen]);

  const formatBytes = (bytes?: number): string => {
    const value = Number(bytes || 0);
    if (value <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    const decimals = unitIndex === 0 ? 0 : size >= 10 ? 1 : 2;
    return `${size.toFixed(decimals)} ${units[unitIndex]}`;
  };

  const formatMonthlyCost = (usd?: number): string => {
    const value = Number(usd || 0);
    if (value <= 0) return '$0.00/mo';
    if (value < 0.01) return '<$0.01/mo';
    return `$${value.toFixed(value < 1 ? 2 : 1)}/mo`;
  };

  const searchedSessions = useMemo(() => {
    const rows = (storageSummary?.sessions || []).filter((item) => (item.totalBytes || 0) > 0);
    const normalizedQuery = query.trim().toLowerCase();

    return rows
      .filter((item) => {
        if (!normalizedQuery) return true;
        const haystack = [
          item.title,
          item.filename,
          item.showTitle,
          item.familyTitle,
          item.familySlug,
          item.sessionId,
          ...(item.arrangementRefs || []).map((ref) => ref.arrangementTitle),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedQuery);
      });
  }, [query, storageSummary]);

  const groupedSessions = useMemo<StorageCleanupGroup[]>(() => {
    const groups = new Map<string, SessionStorageItem[]>();
    for (const item of searchedSessions) {
      const key = item.familySlug ? `family:${item.familySlug}` : `session:${item.sessionId}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)?.push(item);
    }

    return [...groups.entries()]
      .map(([key, members]) => {
        const sortedMembers = [...members].sort((left, right) => {
          const leftVersion = left.familyVersionNumber ?? -1;
          const rightVersion = right.familyVersionNumber ?? -1;
          if (rightVersion !== leftVersion) return rightVersion - leftVersion;
          const bytesDiff = (right.totalBytes || 0) - (left.totalBytes || 0);
          if (bytesDiff !== 0) return bytesDiff;
          return (right.estimatedMonthlyStorageCostUsd || 0) - (left.estimatedMonthlyStorageCostUsd || 0);
        });

        const visibleMembers = sortedMembers.filter((item) => (
          !hideArrangementBlocked || item.cleanupReason !== 'blocked_by_arrangements'
        ));
        if (visibleMembers.length === 0) {
          return null;
        }

        const latest = sortedMembers[0];
        const totalBytes = sortedMembers.reduce((sum, item) => sum + Number(item.totalBytes || 0), 0);
        const totalCostUsd = sortedMembers.reduce((sum, item) => sum + Number(item.estimatedMonthlyStorageCostUsd || 0), 0);
        const eligibleBytes = sortedMembers.reduce(
          (sum, item) => sum + (item.hostedMediaDeleteEligible ? Number(item.totalBytes || 0) : 0),
          0
        );
        const eligibleCount = sortedMembers.filter((item) => item.hostedMediaDeleteEligible).length;
        const arrangementBlockedCount = sortedMembers.filter((item) => item.arrangementRefCount > 0).length;
        const blockedHiddenCount = hideArrangementBlocked
          ? sortedMembers.filter((item) => item.cleanupReason === 'blocked_by_arrangements').length
          : 0;
        const selectableMemberIds = visibleMembers
          .filter((item) => item.hostedMediaDeleteEligible)
          .map((item) => item.sessionId);

        return {
          key,
          isFamily: !!latest?.familySlug && sortedMembers.length > 1,
          familySlug: latest?.familySlug || null,
          title: (!!latest?.familySlug && sortedMembers.length > 1)
            ? (latest?.familyTitle || latest?.title || latest?.sessionId)
            : (latest?.title || latest?.sessionId),
          latest,
          members: sortedMembers,
          visibleMembers,
          totalBytes,
          totalCostUsd,
          eligibleBytes,
          eligibleCount,
          arrangementBlockedCount,
          blockedHiddenCount,
          selectableMemberIds,
        };
      })
      .filter((group): group is StorageCleanupGroup => group !== null)
      .sort((left, right) => {
        const eligibleDiff = right.eligibleBytes - left.eligibleBytes;
        if (eligibleDiff !== 0) return eligibleDiff;
        const eligibleCountDiff = right.eligibleCount - left.eligibleCount;
        if (eligibleCountDiff !== 0) return eligibleCountDiff;
        const totalBytesDiff = right.totalBytes - left.totalBytes;
        if (totalBytesDiff !== 0) return totalBytesDiff;
        return right.totalCostUsd - left.totalCostUsd;
      });
  }, [hideArrangementBlocked, searchedSessions]);

  const selectableSessions = useMemo(
    () => groupedSessions.flatMap((group) => group.visibleMembers).filter((item) => item.hostedMediaDeleteEligible),
    [groupedSessions]
  );

  const selectedSessions = useMemo(
    () => searchedSessions.filter((item) => selectedSessionIds.has(item.sessionId)),
    [searchedSessions, selectedSessionIds]
  );

  const selectedBytes = useMemo(
    () => selectedSessions.reduce((sum, item) => sum + Number(item.totalBytes || 0), 0),
    [selectedSessions]
  );

  const selectedCost = useMemo(
    () => selectedSessions.reduce((sum, item) => sum + Number(item.estimatedMonthlyStorageCostUsd || 0), 0),
    [selectedSessions]
  );

  const toggleSelection = (sessionId: string) => {
    setSelectedSessionIds((current) => {
      const next = new Set(current);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const selectRecommended = () => {
    setSelectedSessionIds(new Set(selectableSessions.map((item) => item.sessionId)));
  };

  const clearSelection = () => setSelectedSessionIds(new Set());

  const toggleGroupSelection = (group: StorageCleanupGroup) => {
    if (group.selectableMemberIds.length === 0) return;
    setSelectedSessionIds((current) => {
      const next = new Set(current);
      const allSelected = group.selectableMemberIds.every((sessionId) => next.has(sessionId));
      for (const sessionId of group.selectableMemberIds) {
        if (allSelected) next.delete(sessionId);
        else next.add(sessionId);
      }
      return next;
    });
  };

  const handleDelete = async () => {
    if (selectedSessionIds.size === 0 || isDeleting) return;
    if (!window.confirm(`Delete MLST-hosted session video for ${selectedSessionIds.size} session(s)? This preserves audio, transcript, metadata, and share links. It only removes hosted video copies that are no longer needed.`)) {
      return;
    }

    setIsDeleting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const selectedById = new Map(selectedSessions.map((item) => [item.sessionId, item]));
      const result = await sessionService.deleteHostedVideoMediaBatch([...selectedSessionIds], token);
      const deletedFallbackBytes = result.deletedSessionIds.reduce(
        (sum, sessionId) => sum + Number(selectedById.get(sessionId)?.totalBytes || 0),
        0
      );
      const reclaimedBytes = result.deletedTotalBytes > 0 ? result.deletedTotalBytes : deletedFallbackBytes;

      if (result.deletedSessionIds.length === 0) {
        const skippedReason = result.skipped.length > 0
          ? ` ${result.skipped.length} session(s) were skipped by the safety checks.`
          : '';
        setError(`No hosted video was deleted.${skippedReason}`);
      } else {
        const deletedPrefix = reclaimedBytes > 0
          ? `Deleted ${formatBytes(reclaimedBytes)}`
          : 'Deleted hosted video';
        const skippedSuffix = result.skipped.length > 0
          ? ` ${result.skipped.length} skipped.`
          : '';
        setSuccessMessage(`${deletedPrefix} across ${result.deletedSessionIds.length} session(s).${skippedSuffix}`);
      }
      setSelectedSessionIds(new Set());
      await loadSummary();
      window.dispatchEvent(new CustomEvent('rescript-session-storage-updated'));
    } catch (err: any) {
      setError(err.message || 'Failed to delete hosted media');
    } finally {
      setIsDeleting(false);
    }
  };

  const getReasonLabel = (item: SessionStorageItem): string => {
    switch (item.cleanupReason) {
      case 'safe_to_delete':
      case 'safe_direct_youtube':
      case 'safe_newer_family_hosted':
      case 'safe_newer_family_youtube':
      case 'safe_newer_family_published':
        return 'Safe to delete';
      case 'blocked_by_arrangements':
        return 'Blocked by arrangements';
      case 'no_youtube_fallback':
      case 'no_video_fallback':
        return 'No video fallback';
      case 'no_hosted_media':
        return 'No hosted media';
      default:
        return 'Needs review';
    }
  };

  const getReasonTone = (item: SessionStorageItem): string => {
    switch (item.cleanupReason) {
      case 'safe_to_delete':
      case 'safe_direct_youtube':
      case 'safe_newer_family_hosted':
      case 'safe_newer_family_youtube':
      case 'safe_newer_family_published':
        return isDarkMode
          ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'
          : 'border-emerald-200 bg-emerald-50 text-emerald-700';
      case 'blocked_by_arrangements':
        return isDarkMode
          ? 'border-amber-400/30 bg-amber-500/10 text-amber-200'
          : 'border-amber-200 bg-amber-50 text-amber-700';
      case 'no_video_fallback':
      case 'no_youtube_fallback':
        return isDarkMode
          ? 'border-slate-500/30 bg-slate-500/10 text-slate-200'
          : 'border-slate-200 bg-slate-100 text-slate-700';
      default:
        return isDarkMode
          ? 'border-slate-500/30 bg-slate-500/10 text-slate-200'
          : 'border-slate-200 bg-slate-100 text-slate-700';
    }
  };

  const shellClassName = isDarkMode
    ? 'border-[#344052] bg-[#232E3F] text-[#F4F4F4]'
    : 'border-[#D6DCE4] bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(244,244,244,0.96))] text-[#232E3F]';
  const panelClassName = isDarkMode
    ? 'border-[#344052] bg-[#202A3A]/90 text-[#F4F4F4]'
    : 'border-[#D6DCE4] bg-white/[0.92] text-[#232E3F]';
  const mutedTextClassName = isDarkMode ? 'text-[#A9B4C3]' : 'text-[#6B788B]';
  const inputClassName = isDarkMode
    ? 'border-white/[0.12] bg-white/[0.08] text-[#F4F4F4] placeholder:text-white/[0.4]'
    : 'border-[#D6DCE4] bg-white/[0.92] text-[#232E3F] placeholder:text-[#7B8798]';

  const toggleGroup = (groupKey: string) => {
    setExpandedGroupKeys((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const getScopeLabel = (item: SessionStorageItem): string => {
    switch (item.cleanupScope) {
      case 'source_and_proxy':
        return 'Deletes hosted source + proxy';
      case 'source':
        return 'Deletes hosted source video';
      case 'proxy':
        return 'Deletes hosted proxy only';
      default:
        return 'No hosted video to remove';
    }
  };

  const getCleanupCopy = (item: SessionStorageItem): string => {
    switch (item.cleanupReason) {
      case 'safe_direct_youtube':
        return 'Safe to delete: this only removes MLST-hosted video for this session. Audio, transcript, and metadata stay intact, and YouTube already covers video playback.';
      case 'safe_newer_family_youtube':
        return `Safe to delete: a newer family version${item.newerFamilyVersionNumber ? ` (v${item.newerFamilyVersionNumber})` : ''} already has YouTube playback, so this older hosted video copy can be removed.`;
      case 'safe_newer_family_hosted':
        return `Safe to delete: a newer family version${item.newerFamilyVersionNumber ? ` (v${item.newerFamilyVersionNumber})` : ''} still has hosted video playback, so this older hosted copy is redundant.`;
      case 'safe_newer_family_published':
        return `Likely safe to delete: a newer family version${item.newerFamilyVersionNumber ? ` (v${item.newerFamilyVersionNumber})` : ''} is already published, so this older hosted video copy is probably redundant.`;
      case 'blocked_by_arrangements':
        return 'Blocked: active arrangements still reference this session’s video source. Deleting it would remove MLST-hosted video for those clips and make the arrangement harder to maintain.';
      case 'no_video_fallback':
        return 'Keep for now: deleting this would only remove in-app video playback, but there is no YouTube or newer published family version to fall back to yet.';
      case 'no_hosted_media':
        return 'Nothing to clean up: no MLST-hosted session video remains for this session.';
      default:
        return 'Needs review before deleting any hosted video.';
    }
  };

  const renderSessionCard = (
    item: SessionStorageItem,
    options: {
      nested?: boolean;
      groupTitle?: string;
    } = {}
  ) => {
    const { nested = false, groupTitle } = options;
    const isSelected = selectedSessionIds.has(item.sessionId);
    const canSelect = item.hostedMediaDeleteEligible;
    const showExternalShow = item.showSlug && item.showSlug !== 'mlst';
    const displayTitle = item.title && item.title !== groupTitle ? item.title : null;

    return (
      <div
        key={item.sessionId}
        className={`rounded-[22px] border px-4 py-4 shadow-[0_18px_34px_-26px_rgba(35,46,63,0.3)] ${
          isDarkMode ? 'border-[#344052] bg-[#182231]/94' : 'border-[#D6DCE4] bg-white/[0.95]'
        } ${nested ? 'ml-0 md:ml-5' : ''}`}
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={isSelected}
              disabled={!canSelect}
              onChange={() => toggleSelection(item.sessionId)}
              className="mt-1 h-4 w-4 rounded border-gray-300"
            />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {item.familyVersionNumber ? (
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                    isDarkMode ? 'border-white/[0.12] bg-white/[0.08] text-white/85' : 'border-[#D6DCE4] bg-[#F8F8F7] text-[#445067]'
                  }`}>
                    v{item.familyVersionNumber}
                  </span>
                ) : null}
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getReasonTone(item)}`}>
                  {getReasonLabel(item)}
                </span>
                {item.hasYoutubeVideo && (
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
                    isDarkMode ? 'border-red-400/30 bg-red-500/10 text-red-200' : 'border-red-200 bg-red-50 text-red-700'
                  }`}>
                    <Youtube className="h-3 w-3" />
                    YouTube
                  </span>
                )}
                {item.publishedToHomepage && (
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
                    isDarkMode ? 'border-brand-yellow/30 bg-brand-yellow/10 text-brand-yellow' : 'border-[#F2D9A6] bg-[#FFF6DB] text-[#A36A00]'
                  }`}>
                    <Sparkles className="h-3 w-3" />
                    Published
                  </span>
                )}
              </div>

              {displayTitle ? (
                <p className="mt-2 font-headlines text-sm font-semibold">{displayTitle}</p>
              ) : null}

              <div className={`mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-body text-xs ${mutedTextClassName}`}>
                <span>{item.sessionId}</span>
                {showExternalShow ? <span>{item.showTitle}</span> : null}
              </div>
            </div>
          </div>
          <div className="grid gap-3 font-body text-sm lg:min-w-[300px] lg:grid-cols-2">
            <div>
              <div className={`font-headlines text-[10px] uppercase tracking-[0.16em] ${mutedTextClassName}`}>Hosted video</div>
              <div className="font-headlines text-base font-semibold">{formatBytes(item.totalBytes)}</div>
              <div className={`text-xs ${mutedTextClassName}`}>Source {formatBytes(item.sourceBytes)} · Proxy {formatBytes(item.proxyBytes)}</div>
            </div>
            <div>
              <div className={`font-headlines text-[10px] uppercase tracking-[0.16em] ${mutedTextClassName}`}>Est. monthly</div>
              <div className="font-headlines text-base font-semibold">{formatMonthlyCost(item.estimatedMonthlyStorageCostUsd)}</div>
              <div className={`text-xs ${mutedTextClassName}`}>{getScopeLabel(item)}</div>
            </div>
          </div>
        </div>

        <div className="mt-3 space-y-3">
          <div className={`rounded-2xl border px-3 py-3 text-sm ${isDarkMode ? 'border-[#344052] bg-[#101925]/72' : 'border-[#D6DCE4] bg-[#F8F8F7]'}`}>
            <div className="mb-2 flex items-center gap-2 font-semibold">
              {item.hostedMediaDeleteEligible ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              )}
              Video cleanup status
            </div>
            <p className={`font-body text-sm ${mutedTextClassName}`}>{getCleanupCopy(item)}</p>
          </div>

          {item.arrangementShouldUpgradeToLatestFamilyVersion ? (
            <div className={`rounded-2xl border px-3 py-3 text-sm ${isDarkMode ? 'border-amber-400/30 bg-amber-500/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              <div className="mb-1 flex items-center gap-2 font-semibold">
                <ShieldAlert className="h-4 w-4" />
                Arrangement update recommended
              </div>
              <p className="font-body text-sm">
                This session is part of a family, but an arrangement still points at this older version.
                {item.familyLatestVersionNumber ? ` Latest family version is v${item.familyLatestVersionNumber}.` : ''}
              </p>
            </div>
          ) : null}

          {item.arrangementRefs && item.arrangementRefs.length > 0 ? (
            <div className={`rounded-2xl border px-3 py-3 text-sm ${isDarkMode ? 'border-[#344052] bg-[#101925]/72' : 'border-[#D6DCE4] bg-[#F8F8F7]'}`}>
              <div className="mb-2 flex items-center gap-2 font-semibold">
                <Film className="h-4 w-4 text-[#355E9A]" />
                Arrangement dependencies
              </div>
              <div className={`space-y-1 font-body text-xs ${mutedTextClassName}`}>
                {item.arrangementRefs.map((ref) => (
                  <div key={ref.arrangementId} className="flex items-center justify-between gap-2">
                    <span className="truncate">{ref.arrangementTitle}</span>
                    <span className="shrink-0 uppercase">{ref.arrangementStatus || 'unknown'}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderMetricCard = (label: string, value: string, detail: string, accentClassName: string) => (
    <div className={`rounded-[22px] border px-4 py-4 shadow-[0_20px_36px_-28px_rgba(35,46,63,0.28)] ${panelClassName}`}>
      <p className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${accentClassName}`}>{label}</p>
      <div className="mt-2 font-headlines text-2xl font-bold">{value}</div>
      <p className={`mt-1 font-body text-xs ${mutedTextClassName}`}>{detail}</p>
    </div>
  );

  const hiddenBlockedSessions = groupedSessions.reduce((sum, group) => sum + group.blockedHiddenCount, 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-[#101925]' : 'bg-[#F4F4F4]'}`} />

      <div className={`relative z-10 flex shrink-0 items-center justify-between border-b px-5 py-3 ${shellClassName}`}>
        <div className="flex items-center gap-3">
          <HardDrive size={18} className="text-brand-yellow" />
          <div>
            <p className="font-headlines text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A36A00] dark:text-[#FFCC66]">
              MLST Workspace
            </p>
            <h2 className="font-headlines text-sm font-semibold">Storage Cleanup</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { void loadSummary(); }}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${
              isDarkMode
                ? 'border-white/[0.12] bg-white/[0.08] text-[#F4F4F4] hover:bg-white/[0.14]'
                : 'border-[#D6DCE4] bg-white/[0.92] text-[#232E3F] hover:bg-[#EEF3FF]'
            }`}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {error && <span className="rounded-full bg-red-600/20 px-3 py-1.5 text-xs text-red-300 dark:text-red-200">{error}</span>}
          {successMessage && <span className="rounded-full bg-brand-yellow/15 px-3 py-1.5 text-xs text-brand-yellow">{successMessage}</span>}
          <button
            onClick={onClose}
            className={`rounded-full p-2 transition-colors ${
              isDarkMode ? 'text-gray-400 hover:bg-white/[0.08]' : 'text-gray-600 hover:bg-[#E8ECF1]'
            }`}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className={`relative z-10 flex-1 overflow-y-auto ${isDarkMode ? 'bg-[#101925] text-[#F4F4F4]' : 'bg-[#F4F4F4] text-[#232E3F]'}`}>
        <div className="mx-auto max-w-7xl px-6 pb-10">
          <div className={`sticky top-0 z-20 -mx-6 mb-6 border-b px-6 py-5 backdrop-blur-xl ${isDarkMode ? 'border-white/[0.08] bg-[#101925]/92' : 'border-[#D6DCE4] bg-[#F4F4F4]/94'}`}>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {renderMetricCard(
                'Hosted media',
                formatBytes(storageSummary?.summary.totalBytes),
                `${storageSummary?.summary.sessionsWithHostedMedia || 0} session(s) with MLST-hosted video`,
                'text-[#A36A00] dark:text-[#FFCC66]'
              )}
              {renderMetricCard(
                'Est. monthly',
                formatMonthlyCost(storageSummary?.summary.estimatedMonthlyStorageCostUsd),
                `Source ${formatBytes(storageSummary?.summary.totalSourceBytes)} · Proxy ${formatBytes(storageSummary?.summary.totalProxyBytes)}`,
                'text-[#355E9A] dark:text-[#BCD7FF]'
              )}
              {renderMetricCard(
                'Reclaimable now',
                formatBytes(storageSummary?.summary.hostedMediaDeleteEligibleBytes),
                `${storageSummary?.summary.hostedMediaDeleteEligibleCount || 0} safe deletion candidate(s)`,
                'text-[#2D7250] dark:text-[#9EE0BD]'
              )}
              {renderMetricCard(
                'Selection',
                formatBytes(selectedBytes),
                `${selectedSessionIds.size} selected · ${formatMonthlyCost(selectedCost)}`,
                'text-[#B54747] dark:text-[#FFB3B3]'
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="relative min-w-[280px] flex-1">
                <Search className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${mutedTextClassName}`} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search titles, families, filenames, arrangements"
                  className={`w-full rounded-2xl border py-3 pl-11 pr-4 font-body text-sm ${inputClassName}`}
                />
              </div>

              <button
                type="button"
                onClick={() => setHideArrangementBlocked((current) => !current)}
                className={`rounded-full border px-4 py-2 font-headlines text-sm font-semibold ${
                  hideArrangementBlocked
                    ? (isDarkMode ? 'border-amber-400/30 bg-amber-500/10 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800')
                    : (isDarkMode ? 'border-white/[0.12] bg-white/[0.08] hover:bg-white/[0.14]' : 'border-[#D6DCE4] bg-white/[0.92] hover:bg-[#EEF3FF]')
                }`}
              >
                {hideArrangementBlocked ? 'Hiding arrangement-blocked' : 'Show arrangement-blocked'}
              </button>
              <button
                type="button"
                onClick={selectRecommended}
                className={`rounded-full border px-4 py-2 font-headlines text-sm font-semibold ${
                  isDarkMode ? 'border-white/[0.12] bg-white/[0.08] hover:bg-white/[0.14]' : 'border-[#D6DCE4] bg-white/[0.92] hover:bg-[#EEF3FF]'
                }`}
              >
                Select reclaimable
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className={`rounded-full border px-4 py-2 font-headlines text-sm font-semibold ${
                  isDarkMode ? 'border-white/[0.12] bg-white/[0.08] hover:bg-white/[0.14]' : 'border-[#D6DCE4] bg-white/[0.92] hover:bg-[#EEF3FF]'
                }`}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={selectedSessionIds.size === 0 || isDeleting}
                className="inline-flex items-center gap-2 rounded-full bg-[#B54747] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete selected video copies
              </button>
            </div>

            <div className={`mt-4 flex flex-wrap items-center gap-3 text-xs ${mutedTextClassName}`}>
              <span>Sorted by reclaimable bytes, largest first.</span>
              {hideArrangementBlocked && hiddenBlockedSessions > 0 ? (
                <span>{hiddenBlockedSessions} arrangement-blocked version(s) hidden</span>
              ) : null}
              <span>This tool only removes MLST-hosted video copies. Audio, transcript, metadata, and share links stay intact.</span>
              <span>Monthly cost is an estimate over managed hosted video bytes; the deeper EFS cache audit is tracked separately.</span>
            </div>
          </div>

          {loading ? (
            <div className={`flex min-h-[45vh] items-center justify-center font-body text-sm ${mutedTextClassName}`}>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading hosted media inventory…
            </div>
          ) : groupedSessions.length === 0 ? (
            <div className="flex min-h-[45vh] items-center justify-center">
              <div className={`max-w-md rounded-[24px] border px-6 py-8 text-center shadow-[0_20px_40px_-28px_rgba(35,46,63,0.25)] ${panelClassName}`}>
                <HardDrive className="mx-auto h-8 w-8 text-brand-yellow" />
                <div className="mt-3 font-headlines text-lg font-semibold">No hosted session video matched</div>
                <p className={`mt-2 font-body text-sm ${mutedTextClassName}`}>
                  Nothing currently matches this search and filter combination.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedSessions.map((group) => {
                const isExpanded = expandedGroupKeys.has(group.key);
                const visibleMembers = group.isFamily && !isExpanded
                  ? group.visibleMembers.slice(0, 1)
                  : group.visibleMembers;
                const selectedInGroup = group.selectableMemberIds.filter((sessionId) => selectedSessionIds.has(sessionId)).length;
                const allSelectableInGroupSelected = group.selectableMemberIds.length > 0
                  && selectedInGroup === group.selectableMemberIds.length;

                return (
                  <div
                    key={group.key}
                    className={`rounded-[26px] border p-5 shadow-[0_22px_42px_-30px_rgba(35,46,63,0.35)] ${
                      isDarkMode ? 'border-[#344052] bg-[#162131]/96' : 'border-[#D6DCE4] bg-white/[0.97]'
                    }`}
                  >
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {group.isFamily ? (
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
                              isDarkMode ? 'border-brand-yellow/30 bg-brand-yellow/10 text-brand-yellow' : 'border-[#F2D9A6] bg-[#FFF6DB] text-[#A36A00]'
                            }`}>
                              <Layers3 className="h-3 w-3" />
                              Episode family
                            </span>
                          ) : null}
                          <p className="font-headlines text-lg font-semibold">{group.title}</p>
                        </div>

                        <div className={`mt-2 flex flex-wrap items-center gap-2 text-xs ${mutedTextClassName}`}>
                          <span>{group.members.length} version{group.members.length === 1 ? '' : 's'}</span>
                          <span>{group.eligibleCount} reclaimable</span>
                          {group.arrangementBlockedCount > 0 ? <span>{group.arrangementBlockedCount} blocked by arrangements</span> : null}
                          {group.blockedHiddenCount > 0 ? <span>{group.blockedHiddenCount} hidden by current filter</span> : null}
                        </div>

                        {group.members.some((item) => item.arrangementShouldUpgradeToLatestFamilyVersion) ? (
                          <div className={`mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
                            isDarkMode ? 'border-amber-400/30 bg-amber-500/10 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'
                          }`}>
                            <ShieldAlert className="h-3.5 w-3.5" />
                            An arrangement still points at an older family version
                          </div>
                        ) : null}
                      </div>

                      <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[460px]">
                        <div>
                          <div className={`font-headlines text-[10px] uppercase tracking-[0.16em] ${mutedTextClassName}`}>Reclaimable now</div>
                          <div className="font-headlines text-lg font-semibold">{formatBytes(group.eligibleBytes)}</div>
                          <div className={`text-xs ${mutedTextClassName}`}>{group.eligibleCount} version(s) currently safe</div>
                        </div>
                        <div>
                          <div className={`font-headlines text-[10px] uppercase tracking-[0.16em] ${mutedTextClassName}`}>Hosted storage</div>
                          <div className="font-headlines text-lg font-semibold">{formatBytes(group.totalBytes)}</div>
                          <div className={`text-xs ${mutedTextClassName}`}>Across the full family stack</div>
                        </div>
                        <div>
                          <div className={`font-headlines text-[10px] uppercase tracking-[0.16em] ${mutedTextClassName}`}>Est. monthly</div>
                          <div className="font-headlines text-lg font-semibold">{formatMonthlyCost(group.totalCostUsd)}</div>
                          <div className={`text-xs ${mutedTextClassName}`}>{selectedInGroup > 0 ? `${selectedInGroup} selected in this family` : 'Most recent visible version stays on top'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      {group.selectableMemberIds.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => toggleGroupSelection(group)}
                          className={`rounded-full border px-4 py-2 text-sm font-semibold ${
                            allSelectableInGroupSelected
                              ? (isDarkMode ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700')
                              : (isDarkMode ? 'border-white/[0.12] bg-white/[0.08] hover:bg-white/[0.14]' : 'border-[#D6DCE4] bg-white/[0.92] hover:bg-[#EEF3FF]')
                          }`}
                        >
                          {allSelectableInGroupSelected ? 'Clear family selection' : `Select ${group.selectableMemberIds.length} reclaimable version${group.selectableMemberIds.length === 1 ? '' : 's'}`}
                        </button>
                      ) : (
                        <div className={`rounded-full border px-4 py-2 text-sm ${isDarkMode ? 'border-white/[0.08] bg-white/[0.04] text-white/60' : 'border-[#D6DCE4] bg-[#F8F8F7] text-[#6B788B]'}`}>
                          No reclaimable versions in this group yet
                        </div>
                      )}

                      {group.isFamily && group.visibleMembers.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.key)}
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold ${
                            isDarkMode ? 'border-white/[0.12] bg-white/[0.08] text-[#F4F4F4]' : 'border-[#D6DCE4] bg-white text-[#232E3F]'
                          }`}
                        >
                          {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          {isExpanded ? 'Collapse family' : `Expand ${group.visibleMembers.length - 1} older version${group.visibleMembers.length - 1 === 1 ? '' : 's'}`}
                        </button>
                      ) : null}
                    </div>

                    <div className="mt-4 space-y-3">
                      {visibleMembers.map((item) => renderSessionCard(item, {
                        nested: group.isFamily,
                        groupTitle: group.title,
                      }))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StorageCleanupManager;
